import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { ref, onValue, push, set, get } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from 'xlsx';

/* -------------------- STATE -------------------- */
const initialProductState = {
  name: "",
  reference: "", 
  barcode: "", 
  price: "",
  purchasePrice: "",
  quantity: "",
  packaging: "",
  saleType: 0,
  minQuantityUnit: 1,
  minQuantityBox: 1,
  
  wholesalePrices: {
    demiGros: { price: "", saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
    gros: { price: "", saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
    superGros: { price: "", saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
  },

  brandId: "",
  brandName: "",
  categoryId: "",
  categoryName: "",

  imageFile: null,
  imageUrl: "",
};


/* -------------------- STYLES -------------------- */
const inputStyle = {
  padding: 8,
  border: "1px solid #ccc",
  borderRadius: 6,
  width: "100%",
  boxSizing: "border-box"
};

const btnStyle = {
  padding: "8px 16px",
  background: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  marginRight: 10,
  marginBottom: 5,
};

const modalStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  background: 'white',
  padding: 30,
  borderRadius: 12,
  maxWidth: 600,
  width: '90%',
  maxHeight: '80vh',
  overflowY: 'auto',
};

const dropzoneStyle = {
  border: '2px dashed #007bff',
  borderRadius: 8,
  padding: 40,
  textAlign: 'center',
  cursor: 'pointer',
  marginBottom: 20,
  backgroundColor: '#f8f9fa',
};

/* -------------------- CSV PARSER -------------------- */
const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  const result = [];
  
  if (lines.length < 2) return result;
  
  let delimiter = ';';
  if (lines[0].includes(';')) {
    delimiter = ';';
  } else if (lines[0].includes(',')) {
    delimiter = ',';
  }
  
  const headers = lines[0].split(delimiter).map(h => 
    h.trim().replace(/"/g, '').replace(/\uFEFF/g, '')
  );
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const obj = {};
    const tempFields = line.split(delimiter);
    
    headers.forEach((header, index) => {
      if (index < tempFields.length) {
        let value = tempFields[index].trim().replace(/"/g, '');
        
        if (header.toLowerCase().includes('prix') || 
            header.toLowerCase().includes('quantite') ||
            header === 'Ventes') {
          value = value.toString().replace(/\s/g, '').replace(',', '.');
          obj[header] = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
        } else {
          obj[header] = value;
        }
      }
    });
    
    if (obj.Nom || obj.nom || obj.NOM) {
      result.push(obj);
    }
  }
  
  return result;
};

/* -------------------- COMPONENT -------------------- */
export default function AddProductPage({ uid: propUid }) {
  const { uid: paramUid } = useParams();
  const uid = propUid || paramUid;
  
  const [product, setProduct] = useState(initialProductState);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [distributorName, setDistributorName] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkProducts, setBulkProducts] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  const [showNewBrandInput, setShowNewBrandInput] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  
  const navigate = useNavigate();
  const fileInputRef = useRef();


// التحقق من Electron - نسخة آمنة
useEffect(() => {
  const checkElectron = async () => {
    // ✅ طريقة آمنة: التحقق من وجود window.electronAPI
    if (window.electronAPI) {
      console.log('✅ Electron API detected, SQLite available');
      try {
        const products = await window.electronAPI.getProducts();
        console.log('📦 Products in SQLite:', products?.length || 0);
      } catch (error) {
        console.error('❌ Error connecting to SQLite:', error);
      }
    } else {
      console.log('⚠️ Not running in Electron, SQLite not available');
      console.log('window.electronAPI:', window.electronAPI);
    }
  };
  checkElectron();
}, []);

useEffect(() => {
  // ✅ اختبار اتصال Electron
  const testElectron = async () => {
    if (window.electronAPI) {
      console.log('✅ Electron API detected!');
      try {
        // اختبار جلب المنتجات
        const products = await window.electronAPI.getProducts();
        console.log('📦 Products in SQLite:', products?.length || 0);
      } catch (error) {
        console.error('❌ Error testing SQLite:', error);
      }
    } else {
      console.log('⚠️ Electron API not found, running in web mode');
    }
  };
  testElectron();
}, []);

useEffect(() => {
    const autoImport = async () => {
      if (window.electronAPI && uid) {
        try {
          const result = await window.electronAPI.importFromFirebase?.(uid);
          if (result?.success && result.count > 0) {
            console.log(`✅ Auto-imported ${result.count} products from Firebase`);
            // تحديث عرض المنتجات
            const products = await window.electronAPI.getProducts();
            console.log('📦 Total products in SQLite:', products?.length);
          }
        } catch (error) {
          console.error('Auto-import error:', error);
        }
      }
    };
    autoImport();
  }, [uid]);

  /* --------- جلب اسم الموزع --------- */
  useEffect(() => {
    if (!uid) return;
    
    const userRef = ref(db, `users/${uid}`);
    const unsub = onValue(userRef, (snap) => {
      const data = snap.val();
      if (data) {
        setDistributorName(data.tradeName || data.name || data.email || "Unknown");
      }
    });
    return () => unsub();
  }, [uid]);

  /* --------- LOAD BRANDS & CATEGORIES (يدعم SQLite) --------- */
useEffect(() => {
  if (!uid) {
    setLoadingData(false);
    return;
  }

  setLoadingData(true);
  
  // ✅ التحقق من Electron
  const isElectron = () => window.electronAPI !== undefined;
  
  // ✅ دالة لتحميل البيانات من SQLite أو Firebase
  const loadData = async () => {
    // 1. محاولة تحميل من SQLite أولاً (إذا كان في Electron)
    if (isElectron()) {
      try {
        const sqliteBrands = await window.electronAPI.getBrands();
        if (sqliteBrands && sqliteBrands.length > 0) {
          console.log('📦 Brands from SQLite:', sqliteBrands.length);
          setBrands(sqliteBrands);
        }
        
        const sqliteCategories = await window.electronAPI.getCategories();
        if (sqliteCategories && sqliteCategories.length > 0) {
          console.log('📦 Categories from SQLite:', sqliteCategories.length);
          setCategories(sqliteCategories);
        }
        
        setLoadingData(false);
      } catch (error) {
        console.error('Error loading from SQLite:', error);
      }
    }
    
    // 2. الاستماع لـ Firebase للمزامنة (دائماً)
    const brandsRef = ref(db, `distributors/${uid}/brands`);
    const catsRef = ref(db, `distributors/${uid}/categories`);

    const unsubBrands = onValue(brandsRef, async (snap) => {
      const v = snap.val();
      let brandsList = [];
      if (v) {
        brandsList = Object.keys(v).map((id) => ({ id, ...v[id] }));
        setBrands(brandsList);
        
        // ✅ مزامنة مع SQLite
        if (isElectron() && brandsList.length > 0) {
          try {
            await window.electronAPI.syncBrands(brandsList);
          } catch (err) {
            console.error('Error syncing brands:', err);
          }
        }
      } else {
        setBrands([]);
      }
      setLoadingData(false);
    });

    const unsubCats = onValue(catsRef, async (snap) => {
      const v = snap.val();
      let categoriesList = [];
      if (v) {
        categoriesList = Object.keys(v).map((id) => ({ id, ...v[id] }));
        setCategories(categoriesList);
        
        // ✅ مزامنة مع SQLite
        if (isElectron() && categoriesList.length > 0) {
          try {
            await window.electronAPI.syncCategories(categoriesList);
          } catch (err) {
            console.error('Error syncing categories:', err);
          }
        }
      } else {
        setCategories([]);
      }
    });

    return () => {
      unsubBrands();
      unsubCats();
    };
  };
  
  loadData();
}, [uid]);

  /* --------- إضافة ماركة جديدة --------- */
  const handleAddNewBrand = async () => {
    if (!newBrandName.trim()) {
      alert("Please enter a brand name");
      return;
    }
    
    try {
      const brandsRef = ref(db, `distributors/${uid}/brands`);
      const newBrandRef = push(brandsRef);
      await set(newBrandRef, {
        name: newBrandName.trim(),
        createdAt: Date.now()
      });
      
      setNewBrandName("");
      setShowNewBrandInput(false);
      alert("Brand added successfully!");
    } catch (error) {
      console.error("Error adding brand:", error);
      alert("Failed to add brand");
    }
  };

  /* --------- إضافة تصنيف جديد --------- */
  const handleAddNewCategory = async () => {
    if (!newCategoryName.trim()) {
      alert("Please enter a category name");
      return;
    }
    
    try {
      const categoriesRef = ref(db, `distributors/${uid}/categories`);
      const newCategoryRef = push(categoriesRef);
      await set(newCategoryRef, {
        name: newCategoryName.trim(),
        createdAt: Date.now()
      });
      
      setNewCategoryName("");
      setShowNewCategoryInput(false);
      alert("Category added successfully!");
    } catch (error) {
      console.error("Error adding category:", error);
      alert("Failed to add category");
    }
  };

  /* --------- VALIDATION FUNCTION --------- */
  const validateForm = () => {
    const errors = {};
    
    if (!product.name.trim()) {
      errors.name = "Le nom du produit est obligatoire";
    }
    
    if (!product.price || isNaN(parseFloat(product.price)) || parseFloat(product.price) <= 0) {
      errors.price = "Le prix de vente doit être un nombre positif";
    }
    
    if (!product.purchasePrice || isNaN(parseFloat(product.purchasePrice)) || parseFloat(product.purchasePrice) < 0) {
      errors.purchasePrice = "Le prix d'achat doit être un nombre valide";
    }
    
    if (!product.quantity || isNaN(parseInt(product.quantity)) || parseInt(product.quantity) < 0) {
      errors.quantity = "La quantité doit être un nombre valide";
    }
    
    if (!product.brandId && !product.brandName) {
      errors.brandId = "La marque est obligatoire";
    }
    
    if (!product.categoryId && !product.categoryName) {
      errors.categoryId = "La catégorie est obligatoire";
    }
    
    if (!product.packaging.trim()) {
      errors.packaging = "Le champ Emballage est obligatoire";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const compressImage = (file, quality = 0.6, maxWidth = 600) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = height * (maxWidth / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
              });
              resolve(compressedFile);
            },
            "image/jpeg",
            quality
          );
        };
      };
    });
  };

  // دالة لتوليد SKU تلقائي
// دالة متطورة لتوليد SKU تلقائي
const generateAutoSKU = () => {
  // 1. بادئة من الماركة (أول 3 حروف)
  let brandPrefix = "PRD";
  if (product.brandName && product.brandName.trim()) {
    brandPrefix = product.brandName.trim().substring(0, 3).toUpperCase();
    // إزالة الأحرف الخاصة
    brandPrefix = brandPrefix.replace(/[^A-Z0-9]/g, '');
  }
  
  // 2. بادئة من التصنيف (أول 2 حروف)
  let categoryPrefix = "";
  if (product.categoryName && product.categoryName.trim()) {
    categoryPrefix = product.categoryName.trim().substring(0, 2).toUpperCase();
    categoryPrefix = categoryPrefix.replace(/[^A-Z0-9]/g, '');
  }
  
  // 3. من اسم المنتج (أول 3 حروف)
  let namePrefix = "";
  if (product.name && product.name.trim()) {
    namePrefix = product.name.trim().substring(0, 3).toUpperCase();
    namePrefix = namePrefix.replace(/[^A-Z0-9]/g, '');
  }
  
  // 4. رقم فريد (timestamp + random)
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  
  // تجميع المكونات
  const parts = [brandPrefix, categoryPrefix, namePrefix].filter(p => p);
  const prefix = parts.join('-');
  
  if (prefix) {
    return `${prefix}-${timestamp}${random}`;
  }
  
  // إذا لم توجد معلومات كافية، استخدم SKU بسيط
  return `SKU-${timestamp}${random}`;
};

// توليد Code-barres EAN-13 (13 رقم)
const generateBarcode = () => {
  // 1. بادئة البلد (690-699 للصين، 590 للبولندا، إلخ)
  // يمكنك اختيار 613 للجزائر مثلاً
  const countryCode = "613"; // الجزائر
  
  // 2. كود الشركة (4-5 أرقام)
  const companyCode = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  
  // 3. كود المنتج (4-5 أرقام)
  const productCode = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  
  // 4. الأرقام الـ 12 الأولى
  const first12 = countryCode + companyCode + productCode;
  
  // 5. حساب رقم التحقق (checksum) لـ EAN-13
  const calculateChecksum = (code) => {
    let sum = 0;
    for (let i = 0; i < code.length; i++) {
      const digit = parseInt(code[i]);
      if (i % 2 === 0) {
        sum += digit * 1;
      } else {
        sum += digit * 3;
      }
    }
    const checksum = (10 - (sum % 10)) % 10;
    return checksum;
  };
  
  const checksum = calculateChecksum(first12);
  
  return first12 + checksum;
};

// التحقق من صحة Code-barres
const validateBarcode = (barcode) => {
  if (!/^\d{13}$/.test(barcode)) return false;
  
  const first12 = barcode.slice(0, 12);
  const checksum = parseInt(barcode[12]);
  
  let sum = 0;
  for (let i = 0; i < first12.length; i++) {
    const digit = parseInt(first12[i]);
    if (i % 2 === 0) {
      sum += digit * 1;
    } else {
      sum += digit * 3;
    }
  }
  const calculatedChecksum = (10 - (sum % 10)) % 10;
  
  return checksum === calculatedChecksum;
};

/* ============== FUNCTIONS FOR SQLITE (ELECTRON) ============== */

// التحقق مما إذا كان التطبيق يعمل في Electron
const isElectron = () => {
   return window && window.electronAPI !== undefined;
};

// الحصول على واجهة Electron
const getElectronAPI = () => {
  if (isElectron()) {
    return window.require('electron').ipcRenderer;
  }
  return null;
};

// حفظ منتج في SQLite المحلي
// حفظ منتج في SQLite المحلي (فقط في Electron)
// حفظ منتج في SQLite المحلي - نسخة آمنة (بدون require)
const saveProductToLocalDB = async (productData) => {
  // ✅ التحقق من وجود Electron API عبر window
  const hasElectronAPI = typeof window !== 'undefined' && window.electronAPI;
  
  if (!hasElectronAPI) {
    console.log('⚠️ Not in Electron or Electron API not available, skipping SQLite save');
    return null;
  }
  
  try {
    const productId = `prod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const productWithId = {
      id: productId,
      ...productData,
      syncStatus: 'pending'
    };
    
    console.log('📤 Sending to SQLite via window.electronAPI...');
    const result = await window.electronAPI.saveProduct(productWithId);
    
    if (result && result.success) {
      console.log('✅ Saved to SQLite:', result.id);
      return productId;
    } else {
      console.error('❌ SQLite save failed:', result?.error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error saving to SQLite:', error);
    return null;
  }
};
 
/* ---------------- SINGLE PRODUCT SUBMIT ---------------- */
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!uid) {
    alert("No distributor ID found!");
    return;
  }
  
  if (!validateForm()) {
    alert("Veuillez corriger les erreurs dans le formulaire");
    return;
  }
  
  try {
    setIsLoading(true);
    
    // 1. معالجة الصورة
    let imageUrl = "";
    if (product.imageFile) {
      const storage = getStorage();
      const compressedImage = await compressImage(product.imageFile);
      const imgRef = storageRef(
        storage,
        `products/${uid}/${Date.now()}_${compressedImage.name}`
      );
      await uploadBytes(imgRef, compressedImage);
      imageUrl = await getDownloadURL(imgRef);
    }

    // 2. حساب القيم
    const saleType = Number(product.saleType);
    const packaging = parseFloat(product.packaging) || 1;
    
    let finalPrice, finalQuantity, finalPurchasePrice;
    const inputPrice = parseFloat(product.price);
    const inputQuantity = parseInt(product.quantity);
    const inputPurchasePrice = parseFloat(product.purchasePrice) || 0;
    
    if (saleType === 1) {
      finalPrice = inputPrice / packaging;
      finalQuantity = inputQuantity * packaging;
      finalPurchasePrice = inputPurchasePrice / packaging;
    } else {
      finalPrice = inputPrice;
      finalQuantity = inputQuantity;
      finalPurchasePrice = inputPurchasePrice;
    }
    
    let minQuantity = 0;
    let minBoxQuantity = 0;
    
    if (saleType === 0) {
      minQuantity = Number(product.minQuantityUnit) || 1;
      minBoxQuantity = 0;
    } else {
      minQuantity = 0;
      minBoxQuantity = Number(product.minQuantityBox) || 1;
    }

    const wholesalePricesData = {
      demiGros: {
        price: Number(product.wholesalePrices?.demiGros?.price || 0),
        saleType: Number(product.wholesalePrices?.demiGros?.saleType || 0),
        minQtyUnit: Number(product.wholesalePrices?.demiGros?.minQtyUnit || 1),
        minQtyBox: Number(product.wholesalePrices?.demiGros?.minQtyBox || 1),
      },
      gros: {
        price: Number(product.wholesalePrices?.gros?.price || 0),
        saleType: Number(product.wholesalePrices?.gros?.saleType || 0),
        minQtyUnit: Number(product.wholesalePrices?.gros?.minQtyUnit || 1),
        minQtyBox: Number(product.wholesalePrices?.gros?.minQtyBox || 1),
      },
      superGros: {
        price: Number(product.wholesalePrices?.superGros?.price || 0),
        saleType: Number(product.wholesalePrices?.superGros?.saleType || 0),
        minQtyUnit: Number(product.wholesalePrices?.superGros?.minQtyUnit || 1),
        minQtyBox: Number(product.wholesalePrices?.superGros?.minQtyBox || 1),
      },
    };

    // 3. تحضير بيانات المنتج
    const productData = {
      name: product.name,
      reference: product.reference || generateAutoSKU(),
      barcode: product.barcode || generateBarcode(),
      price: finalPrice,
      purchasePrice: finalPurchasePrice,
      quantity: finalQuantity,
      packaging: product.packaging,
      saleType: saleType,
      minQuantity: minQuantity,
      minBoxQuantity: minBoxQuantity,
      brandId: product.brandId,
      brandName: product.brandName,
      categoryId: product.categoryId,
      categoryName: product.categoryName,
      imageUrl: imageUrl,
      wholesalePrices: wholesalePricesData,
      salesCount: 0,
      createdAt: Date.now(),
    };

    // 4. حفظ في SQLite المحلي (إذا كنا في Electron)
    let localSaved = false;
    const hasElectronAPI = window.electronAPI !== undefined;

    if (hasElectronAPI) {
      try {
        const productId = `prod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const productWithId = {
          id: productId,
          ...productData,
          syncStatus: 'pending'
        };
        
        const result = await window.electronAPI.saveProduct(productWithId);
        
        if (result && result.success) {
          localSaved = true;
          console.log('✅ Saved to SQLite:', result.id);
        }
      } catch (err) {
        console.error('❌ Error saving to SQLite:', err);
      }
    }
    
    // 5. حفظ في Firebase (حتى لو فشل، نكمل)
    let firebaseSaved = false;
    try {
      const listRef = ref(db, `distributors/${uid}/products`);
      const productRef = push(listRef);
      await set(productRef, productData);
      firebaseSaved = true;
      console.log('✅ Saved to Firebase');
    } catch (firebaseError) {
      console.error('❌ Firebase save error:', firebaseError);
    }

    // 6. رسالة مناسبة حسب الوضع
    if (hasElectronAPI && localSaved && !firebaseSaved) {
      // ✅ وضع الأوفلاين: حفظ في SQLite فقط
      alert("✅ Produit enregistré localement (hors ligne) !\n\n" +
            "📌 Le produit a été sauvegardé dans la base de données locale.\n" +
            "🔄 Il sera synchronisé automatiquement avec le cloud lorsque la connexion Internet sera rétablie.\n\n" +
            "👍 Vous pouvez continuer à utiliser l'application normalement.");
    } else if (hasElectronAPI && localSaved && firebaseSaved) {
      // ✅ وضع الاتصال: حفظ في SQLite و Firebase
      alert("✅ Produit enregistré avec succès !\n\n" +
            "✅ Sauvegardé localement (SQLite)\n" +
            "✅ Sauvegardé dans le cloud (Firebase)");
    } else if (!hasElectronAPI && firebaseSaved) {
      // ✅ وضع المتصفح: حفظ في Firebase فقط
      alert("✅ Produit enregistré avec succès dans le cloud !");
    } else {
      alert("⚠️ Produit enregistré partiellement. Vérifiez votre connexion.");
    }
    
    // 7. تنظيف النموذج
    setProduct(initialProductState);
    setFormErrors({});
    
    // 8. العودة إلى صفحة المنتجات
    if (paramUid) {
      navigate(`/user/${uid}/products`);
    } else {
      navigate(`/products`);
    }
    
  } catch (err) {
    console.error(err);
    alert("❌ Une erreur est survenue !\n" + err.message);
  } finally {
    setIsLoading(false);
  }
};
    

  /* ---------------- BULK IMPORT FUNCTIONS ---------------- */
  

 /* -------------------- EXCEL TEMPLATE (SIMPLIFIED) -------------------- */
const downloadExcelTemplate = () => {
  const template = `Nom;Reference;CodeBarres;Prix;Prix_Achat;Quantite;Emballage;Marque;Categorie
Gel Douche 800ML;PRD-BAB-001;6131234567890;595;560;100;24;BABY POWDER;Corps
Shampooing 500ML;PRD-LOR-002;6131234567891;450;400;200;12;L'Oréal;Cheveux
Crème hydratante;PRD-NIV-003;6131234567892;300;250;50;6;Nivea;Visage`;

  const blob = new Blob(["\uFEFF" + template], { 
    type: 'text/csv;charset=utf-8'
  });
  
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "modele_produits.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  alert(
    "📋 MODÈLE TÉLÉCHARGÉ\n\n" +
    "Le fichier contient les colonnes suivantes:\n" +
    "• Nom (obligatoire)\n" +
    "• Reference (optionnel)\n" +
    "• CodeBarres (optionnel)\n" +
    "• Prix (obligatoire)\n" +
    "• Prix_Achat (obligatoire)\n" +
    "• Quantite (obligatoire)\n" +
    "• Emballage (obligatoire)\n" +
    "• Marque (obligatoire)\n" +
    "• Categorie (obligatoire)\n\n" +
    "⚠️ Séparateur: point-virgule (;)\n" +
    "💡 Ouvrez le fichier avec un éditeur de texte pour voir le format"
  );
};

/* -------------------- SIMPLIFIED CSV PARSER -------------------- */
const parseSimpleCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  const result = [];
  
  if (lines.length < 2) {
    console.log("Pas assez de lignes");
    return result;
  }
  
  // Detect delimiter
  let delimiter = ';';
  if (lines[0].includes(';')) {
    delimiter = ';';
  } else if (lines[0].includes(',')) {
    delimiter = ',';
  }
  
  const headers = lines[0].split(delimiter).map(h => 
    h.trim().replace(/"/g, '').replace(/\uFEFF/g, '')
  );
  
  console.log("Headers trouvés:", headers);
  console.log("Delimiter utilisé:", delimiter);
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const obj = {};
    const tempFields = line.split(delimiter);
    
    console.log(`Ligne ${i}:`, tempFields);
    
    headers.forEach((header, index) => {
      if (index < tempFields.length) {
        let value = tempFields[index].trim().replace(/"/g, '');
        const lowerHeader = header.toLowerCase();
        
        // Mapping des colonnes selon le nom (plus flexible)
        if (lowerHeader === 'nom' || lowerHeader === 'name' || lowerHeader === 'produit') {
          obj.name = value;
        }
        else if (lowerHeader === 'reference' || lowerHeader === 'ref' || lowerHeader === 'sku') {
          obj.reference = value;
        }
        else if (lowerHeader === 'codebarres' || lowerHeader === 'code_barres' || lowerHeader === 'barcode' || lowerHeader === 'ean') {
          obj.barcode = value;
        }
        else if (lowerHeader === 'prix' || lowerHeader === 'price' || lowerHeader === 'prix_vente') {
          const numValue = parseFloat(value.toString().replace(/\s/g, '').replace(',', '.'));
          obj.price = isNaN(numValue) ? 0 : numValue;
        }
        else if (lowerHeader === 'prix_achat' || lowerHeader === 'purchaseprice' || lowerHeader === 'prix_achat_unite') {
          const numValue = parseFloat(value.toString().replace(/\s/g, '').replace(',', '.'));
          obj.purchasePrice = isNaN(numValue) ? 0 : numValue;
        }
        else if (lowerHeader === 'quantite' || lowerHeader === 'quantity' || lowerHeader === 'qty' || lowerHeader === 'stock') {
          const numValue = parseInt(value);
          obj.quantity = isNaN(numValue) ? 0 : numValue;
        }
        else if (lowerHeader === 'emballage' || lowerHeader === 'packaging' || lowerHeader === 'conditionnement') {
          obj.packaging = value;
        }
        else if (lowerHeader === 'marque' || lowerHeader === 'brand') {
          obj.brand = value;
        }
        else if (lowerHeader === 'categorie' || lowerHeader === 'category') {
          obj.category = value;
        }
      }
    });
    
    console.log("Objet parsé:", obj);
    
    // Vérifier les champs obligatoires
    const hasRequiredFields = obj.name && obj.name.trim() !== "";
    
    if (hasRequiredFields) {
      // S'assurer que tous les champs existent avec des valeurs par défaut
      result.push({
        name: obj.name || "",
        reference: obj.reference || "",
        barcode: obj.barcode || "",
        price: obj.price || 0,
        purchasePrice: obj.purchasePrice || 0,
        quantity: obj.quantity || 0,
        packaging: obj.packaging || "1",
        brand: obj.brand || "",
        category: obj.category || ""
      });
    } else {
      console.warn(`Ligne ${i} ignorée: pas de nom de produit`);
    }
  }
  
  console.log("Total produits trouvés:", result.length);
  return result;
};

/* -------------------- UPDATED BULK IMPORT HANDLER -------------------- */
const handleBulkFileUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  setErrorMessage("");
  setBulkProducts([]);

  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      let csvText = e.target.result;
      
      if (csvText.charCodeAt(0) === 0xFEFF) {
        csvText = csvText.substring(1);
      }
      
      const parsedData = parseSimpleCSV(csvText);
      
      if (parsedData.length === 0) {
        setErrorMessage("Aucun produit valide trouvé dans le fichier. Vérifiez les colonnes: Nom, Prix, Prix_Achat, Quantite, Emballage, Marque, Categorie");
        return;
      }

      setBulkProducts(parsedData);
      alert(`✅ ${parsedData.length} produits prêts à l'importation`);
      
    } catch (error) {
      console.error("Erreur:", error);
      setErrorMessage("Format de fichier invalide. Utilisez le modèle fourni.");
    }
  };
  
  reader.onerror = () => {
    setErrorMessage("Erreur de lecture du fichier");
  };
  
  reader.readAsText(file, 'UTF-8');
};

/* ---------------- SIMPLIFIED BULK IMPORT PROCESS (UPDATED) ---------------- */
const processBulkImport = async () => {
  if (bulkProducts.length === 0) return;

  if (!window.confirm(`Importer ${bulkProducts.length} produits avec les valeurs par défaut ?`)) return;

  setIsImporting(true);
  setImportProgress(0);
  setErrorMessage("");

  let successCount = 0;
  let errorCount = 0;

  try {
    const brandsRef = ref(db, `distributors/${uid}/brands`);
    const categoriesRef = ref(db, `distributors/${uid}/categories`);
    
    const [brandsSnap, categoriesSnap] = await Promise.all([
      get(brandsRef),
      get(categoriesRef)
    ]);
    
    const existingBrands = brandsSnap.val() || {};
    const existingCategories = categoriesSnap.val() || {};

    const brandMap = {};
    Object.keys(existingBrands).forEach(id => {
      if (existingBrands[id].name) {
        brandMap[existingBrands[id].name.toLowerCase()] = id;
      }
    });

    const categoryMap = {};
    Object.keys(existingCategories).forEach(id => {
      if (existingCategories[id].name) {
        categoryMap[existingCategories[id].name.toLowerCase()] = id;
      }
    });

    for (let i = 0; i < bulkProducts.length; i++) {
      const row = bulkProducts[i];
      
      try {
        const productId = push(ref(db, `distributors/${uid}/products`)).key;
        
        const productName = row.name;
        
        if (!productName || !productName.trim()) {
          console.warn(`Ligne ${i+1}: Nom du produit vide, ignoré`);
          errorCount++;
          continue;
        }
        
        // Get values with defaults
        const reference = row.reference || "";       
        const barcode = row.barcode || "";  
        const price = parseFloat(row.price) || 0;
        const purchasePrice = parseFloat(row.purchasePrice) || 0;
        const quantity = parseInt(row.quantity) || 0;
        const packaging = row.packaging || "1";
        const brandName = row.brand || "";
        const categoryName = row.category || "";
        
        // 🔑 IMPORTANT: Par défaut, on considère que l'import CSV est en UNITÉ
        // Les valeurs sont déjà en unités
        const saleType = 0; // Vente à l'unité par défaut
        const packagingNum = parseFloat(packaging) || 1;
        
        // Handle brand
        let brandId = "";
        let finalBrandName = brandName;
        
        if (brandName) {
          const lowerBrandName = brandName.toLowerCase().trim();
          if (brandMap[lowerBrandName]) {
            brandId = brandMap[lowerBrandName];
          } else {
            const newBrandId = push(ref(db, `distributors/${uid}/brands`)).key;
            await set(ref(db, `distributors/${uid}/brands/${newBrandId}`), {
              name: brandName,
              createdAt: Date.now()
            });
            brandId = newBrandId;
            brandMap[lowerBrandName] = newBrandId;
          }
        }

        // Handle category
        let categoryId = "";
        let finalCategoryName = categoryName;
        
        if (categoryName) {
          const lowerCategoryName = categoryName.toLowerCase().trim();
          if (categoryMap[lowerCategoryName]) {
            categoryId = categoryMap[lowerCategoryName];
          } else {
            const newCategoryId = push(ref(db, `distributors/${uid}/categories`)).key;
            await set(ref(db, `distributors/${uid}/categories/${newCategoryId}`), {
              name: categoryName,
              createdAt: Date.now()
            });
            categoryId = newCategoryId;
            categoryMap[lowerCategoryName] = newCategoryId;
          }
        }

        // Create product (les valeurs sont déjà en unités)
        const productData = {
          name: productName,
          reference: reference,     // ✅ استخدم المتغير reference
          barcode: barcode,         // ✅ استخدم المتغير barcode
          price: price,                           // Prix unitaire
          purchasePrice: purchasePrice,           // Prix d'achat unitaire
          quantity: quantity,                     // Quantité en unités
          packaging: packaging,                   // Emballage
          
          saleType: saleType,                     // 0 = unité
          minQuantity: 1,                         // Minimum 1 unité par défaut
          minBoxQuantity: 0,                      // Pas de carton
          
          brandId: brandId,
          brandName: finalBrandName,
          categoryId: categoryId,
          categoryName: finalCategoryName,
          
          imageUrl: "",
          
          wholesalePrices: {
            demiGros: { price: 0, saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
            gros: { price: 0, saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
            superGros: { price: 0, saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
          },
          
          salesCount: 0,
          createdAt: Date.now(),
        };

        await set(ref(db, `distributors/${uid}/products/${productId}`), productData);
        successCount++;
        
      } catch (error) {
        console.error(`Erreur produit ${i}:`, error);
        errorCount++;
      }
      
      setImportProgress(Math.round(((i + 1) / bulkProducts.length) * 100));
    }

    alert(
      `✅ IMPORTATION TERMINÉE\n\n` +
      `Produits ajoutés: ${successCount}\n` +
      `Produits ignorés/erreurs: ${errorCount}`
    );

    setBulkProducts([]);
    setShowBulkModal(false);

    setTimeout(() => {
      window.location.reload();
    }, 1500);

  } catch (error) {
    console.error("Erreur générale:", error);
    setErrorMessage("Erreur lors de l'importation: " + error.message);
  } finally {
    setIsImporting(false);
    setImportProgress(0);
  }
};
  /* ---------------- CHECK IF FORM IS VALID ---------------- */
  const isFormValid = () => {
    return (
      product.name.trim() &&
      product.price && !isNaN(parseFloat(product.price)) && parseFloat(product.price) > 0 &&
      product.purchasePrice && !isNaN(parseFloat(product.purchasePrice)) && parseFloat(product.purchasePrice) >= 0 &&
      product.quantity && !isNaN(parseInt(product.quantity)) && parseInt(product.quantity) >= 0 &&
      (product.brandId || product.brandName) &&
      (product.categoryId || product.categoryName) &&
      product.packaging.trim()
    );
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 20 }}>Ajouter des Produits</h2>

      {/* عرض اسم الموزع */}
      {uid && distributorName && (
        <div style={{
          background: "#e0f2fe",
          padding: "12px 16px",
          borderRadius: 8,
          marginBottom: 20,
          borderLeft: "4px solid #0891b2"
        }}>
          <strong>📦 Ajout des produits pour distributor:</strong> {distributorName}
          <div style={{ fontSize: "12px", color: "#6b7280", marginTop: 4 }}>
            Distributor ID: {uid}
          </div>
        </div>
      )}

      {/* Bulk Import Section */}
      <div style={{ marginBottom: 30, padding: 20, background: "#f8f9fa", borderRadius: 8 }}>
        <h3 style={{ color: '#28a745', marginBottom: 15 }}>
          📤 Importation en Masse depuis Excel/CSV
        </h3>
        
        <div style={{ marginBottom: 15 }}>
          <button
            onClick={downloadExcelTemplate}
            style={{
              ...btnStyle,
              background: "#fd7e14",
              fontWeight: 'bold',
              padding: '10px 20px'
            }}
          >
            📥 Télécharger Modèle
          </button>
          
          <button
            onClick={() => setShowBulkModal(true)}
            style={{
              ...btnStyle,
              background: "#28a745",
              fontWeight: 'bold',
              padding: '10px 20px',
              marginLeft: 10
            }}
          >
            📤 Importer Fichier CSV
          </button>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div style={modalStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginBottom: 20 }}>Importer depuis CSV</h3>
            
            <div 
              style={dropzoneStyle}
              onClick={() => fileInputRef.current?.click()}
            >
              <p>📂 Cliquez pour sélectionner votre fichier CSV</p>
              <p style={{ fontSize: "12px", color: "#666" }}>Format accepté: .csv</p>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleBulkFileUpload}
                style={{ display: 'none' }}
              />
            </div>

           {bulkProducts.length > 0 && (
  <div style={{ marginBottom: 20 }}>
    <p><strong>{bulkProducts.length}</strong> produits trouvés</p>
    <div style={{ 
      maxHeight: 150, 
      overflowY: 'auto', 
      border: '1px solid #ddd', 
      padding: 10,
      borderRadius: 6,
      fontSize: 12
    }}>
      {bulkProducts.slice(0, 5).map((row, i) => (
        <div key={i} style={{ marginBottom: 5, borderBottom: '1px solid #eee', paddingBottom: 5 }}>
          <div>
            <strong>📦 {row.name}</strong> - 💰 {row.price} DA
          </div>
          <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>
            {row.reference && <span>🔖 {row.reference} </span>}
            {row.barcode && <span>📷 {row.barcode} </span>}
            {row.brand && <span>🏷️ {row.brand} </span>}
            {row.category && <span>📁 {row.category}</span>}
          </div>
        </div>
      ))}
      {bulkProducts.length > 5 && (
        <p style={{ textAlign: 'center', color: '#666', marginTop: 10 }}>
          ... et {bulkProducts.length - 5} autres
        </p>
      )}
    </div>
  </div>
)}

            {errorMessage && (
              <div style={{ 
                color: '#dc3545', 
                marginBottom: 20,
                padding: 10,
                backgroundColor: '#f8d7da',
                borderRadius: 6
              }}>
                ❌ {errorMessage}
              </div>
            )}

            {isImporting && (
              <div style={{ marginBottom: 20 }}>
                <p>Importation en cours... {importProgress}%</p>
                <div style={{ 
                  width: '100%', 
                  height: 10, 
                  background: '#e9ecef', 
                  borderRadius: 5 
                }}>
                  <div 
                    style={{ 
                      width: `${importProgress}%`, 
                      height: '100%', 
                      background: '#28a745',
                      borderRadius: 5
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                style={{ ...btnStyle, background: '#6c757d' }}
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkProducts([]);
                  setErrorMessage("");
                }}
                disabled={isImporting}
              >
                Annuler
              </button>
              
              <button
                style={{ ...btnStyle, background: '#28a745' }}
                onClick={processBulkImport}
                disabled={bulkProducts.length === 0 || isImporting}
              >
                {isImporting ? 'Importation...' : 'Importer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ SECTION 1: TYPE DE VENTE (في الأعلى - جديد) */}
      <div style={{ 
        marginBottom: 20, 
        padding: 16, 
        background: "#e3f2fd", 
        borderRadius: 8,
        border: "1px solid #2196f3"
      }}>
        <label style={{ fontWeight: "bold", marginBottom: 10, display: "block" }}>
          📦 Type de vente <span style={{ color: 'red' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="radio"
              name="saleType"
              value="0"
              checked={product.saleType === 0}
              onChange={() => {
                setProduct({ 
                  ...product, 
                  saleType: 0,
                  price: "",
                  purchasePrice: "",
                  quantity: ""
                });
              }}
              style={{ width: 18, height: 18 }}
            />
            <span>🏷️ Vente à l'unité</span>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="radio"
              name="saleType"
              value="1"
              checked={product.saleType === 1}
              onChange={() => {
                setProduct({ 
                  ...product, 
                  saleType: 1,
                  price: "",
                  purchasePrice: "",
                  quantity: ""
                });
              }}
              style={{ width: 18, height: 18 }}
            />
            <span>📦 Vente par carton</span>
          </label>
        </div>
        
        {product.packaging && product.saleType === 1 && (
          <div style={{ 
            fontSize: 12, 
            color: "#0891b2", 
            marginTop: 8,
            padding: 6,
            background: "#e0f2fe",
            borderRadius: 4
          }}>
            ℹ️ 1 carton = {product.packaging} unités
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>

        {/* Product Info */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {/* Nom */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Nom de produit <span style={{ color: 'red' }}>*</span></label>
            <input
              placeholder="Nom"
              value={product.name}
              onChange={(e) => setProduct({ ...product, name: e.target.value })}
              style={inputStyle} 
              required
            />
            {formErrors.name && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.name}</small>}
          </div>

          {/* Référence produit (اختياري) */}
<div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
  <label>🔖 Référence produit (SKU)</label>
  <div style={{ display: 'flex', gap: 8 }}>
    <input
      type="text"
      placeholder="Ex: PRD-001, SKU12345 (optionnel)"
      value={product.reference}
      onChange={(e) => setProduct({ ...product, reference: e.target.value })}
      style={{ ...inputStyle, flex: 1 }}
    />
    <button
      type="button"
      onClick={() => {
        const newSKU = generateAutoSKU();
        setProduct({ ...product, reference: newSKU });
        alert(`✅ SKU généré: ${newSKU}`);
      }}
      style={{ ...btnStyle, background: "#6c757d", padding: "8px 12px", marginRight: 0, whiteSpace: "nowrap" }}
      title="Générer un code automatique"
    >
      🔄 Générer
    </button>
  </div>
  <small style={{ fontSize: "11px", color: "#666" }}>
    Code unique pour identifier le produit (optionnel - laisse vide ou génère automatiquement)
  </small>
</div>

{/* Code-barres */}
<div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
  <label>📷 Code-barres (EAN-13)</label>
  <div style={{ display: 'flex', gap: 8 }}>
    <input
      type="text"
      maxLength="13"
      placeholder="13 chiffres (optionnel)"
      value={product.barcode}
      onChange={(e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 13) {
          setProduct({ ...product, barcode: value });
        }
      }}
      style={{ ...inputStyle, flex: 1 }}
    />
    <button
      type="button"
      onClick={() => setProduct({ ...product, barcode: generateBarcode() })}
      style={{ ...btnStyle, background: "#28a745", padding: "8px 12px", marginRight: 0 }}
    >
      🔄 Générer EAN-13
    </button>
  </div>
  <small style={{ fontSize: "11px", color: "#666" }}>
    13 chiffres pour scan au point de vente (optionnel)
  </small>
</div>

          {/* Brand */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Marque <span style={{ color: 'red' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={product.brandId}
                onChange={(e) => {
                  const b = brands.find((x) => x.id === e.target.value);
                  if (!b) return;
                  setProduct({ ...product, brandId: b.id, brandName: b.name });
                }}
                style={{ ...inputStyle, flex: 1 }}
              >
                <option value="">Sélectionner une marque</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowNewBrandInput(!showNewBrandInput)}
                style={{ ...btnStyle, background: "#28a745", padding: "8px 12px", marginRight: 0 }}
              >
                +
              </button>
            </div>
            {showNewBrandInput && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  type="text"
                  placeholder="Nouvelle marque"
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button type="button" onClick={handleAddNewBrand} style={{ ...btnStyle, background: "#007bff", padding: "8px 12px", marginRight: 0 }}>Ajouter</button>
                <button type="button" onClick={() => setShowNewBrandInput(false)} style={{ ...btnStyle, background: "#6c757d", padding: "8px 12px", marginRight: 0 }}>Annuler</button>
              </div>
            )}
            {formErrors.brandId && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.brandId}</small>}
          </div>

          {/* Category */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Catégorie <span style={{ color: 'red' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={product.categoryId}
                onChange={(e) => {
                  const c = categories.find((x) => x.id === e.target.value);
                  if (!c) return;
                  setProduct({ ...product, categoryId: c.id, categoryName: c.name });
                }}
                style={{ ...inputStyle, flex: 1 }}
              >
                <option value="">Sélectionner une catégorie</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCategoryInput(!showNewCategoryInput)}
                style={{ ...btnStyle, background: "#28a745", padding: "8px 12px", marginRight: 0 }}
              >
                +
              </button>
            </div>
            {showNewCategoryInput && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  type="text"
                  placeholder="Nouvelle catégorie"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button type="button" onClick={handleAddNewCategory} style={{ ...btnStyle, background: "#007bff", padding: "8px 12px", marginRight: 0 }}>Ajouter</button>
                <button type="button" onClick={() => setShowNewCategoryInput(false)} style={{ ...btnStyle, background: "#6c757d", padding: "8px 12px", marginRight: 0 }}>Annuler</button>
              </div>
            )}
            {formErrors.categoryId && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.categoryId}</small>}
          </div>

          {/* Emballage */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Emballage (pièces/carton) <span style={{ color: 'red' }}>*</span></label>
            <input
              type="number"
              min="1"
              placeholder="Exemple: 24"
              value={product.packaging}
              onChange={(e) => setProduct({ ...product, packaging: e.target.value })}
              style={inputStyle} 
            />
            {formErrors.packaging && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.packaging}</small>}
          </div>

          {/* Purchase Price - HINT متغير */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Prix d'achat (DA) <span style={{ color: 'red' }}>*</span></label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={product.saleType === 0 ? "Prix d'achat (unité)" : "Prix d'achat (carton)"}
              value={product.purchasePrice}
              onChange={(e) => setProduct({ ...product, purchasePrice: e.target.value })}
              style={inputStyle} 
            />
            <small style={{ fontSize: "11px", color: "#666" }}>
              {product.saleType === 0 ? "Prix par unité" : `Prix par carton (${product.packaging || "?"} unités)`}
            </small>
            {formErrors.purchasePrice && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.purchasePrice}</small>}
          </div>

          {/* Sale Price - HINT متغير */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Prix de vente (DA) <span style={{ color: 'red' }}>*</span></label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder={product.saleType === 0 ? "Prix de vente (unité)" : "Prix de vente (carton)"}
              value={product.price}
              onChange={(e) => setProduct({ ...product, price: e.target.value })}
              style={inputStyle} 
            />
            <small style={{ fontSize: "11px", color: "#666" }}>
              {product.saleType === 0 ? "Prix par unité" : `Prix par carton (${product.packaging || "?"} unités)`}
            </small>
            {formErrors.price && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.price}</small>}
          </div>

          {/* Quantity - HINT متغير */}
          <div style={{ flex: '1 1 25%', display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
            <label>Quantité en stock <span style={{ color: 'red' }}>*</span></label>
            <input
              type="number"
              min="0"
              placeholder={product.saleType === 0 ? "Nombre d'unités" : "Nombre de cartons"}
              value={product.quantity}
              onChange={(e) => setProduct({ ...product, quantity: e.target.value })}
              style={inputStyle} 
            />
            <small style={{ fontSize: "11px", color: "#666" }}>
              {product.saleType === 0 
                ? "Nombre total d'unités" 
                : `${product.quantity || 0} carton(s) = ${(parseInt(product.quantity) || 0) * (parseInt(product.packaging) || 1)} unités`}
            </small>
            {formErrors.quantity && <small style={{ color: '#dc3545', fontSize: 12 }}>{formErrors.quantity}</small>}
          </div>
        </div>

        {/* ✅ SECTION 2: QUANTITÉ MINIMALE DE COMMANDE (بدون Select) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, padding: 16, background: "#f8f9fa", borderRadius: 8 }}>
          <h4 style={{ width: '100%', marginTop: 0, marginBottom: 12 }}>Quantité Minimale de Commande</h4>

          {product.saleType === 0 && (
            <div style={{ flex: '1 1 100%' }}>
              <label>Quantité minimum (Unité)</label>
              <input
                type="number"
                min="1"
                value={product.minQuantityUnit}
                onChange={(e) => setProduct({ ...product, minQuantityUnit: parseInt(e.target.value) })}
                style={inputStyle}
              />
              <small style={{ fontSize: "11px", color: "#666" }}>Minimum d'unités à commander</small>
            </div>
          )}

          {product.saleType === 1 && (
            <div style={{ flex: '1 1 100%' }}>
              <label>Quantité minimum (Carton)</label>
              <input
                type="number"
                min="1"
                value={product.minQuantityBox}
                onChange={(e) => setProduct({ ...product, minQuantityBox: parseInt(e.target.value) })}
                style={inputStyle}
              />
              <small style={{ fontSize: "11px", color: "#666" }}>
                Minimum de cartons à commander ({parseInt(product.minQuantityBox) * (parseInt(product.packaging) || 1)} unités)
              </small>
            </div>
          )}
        </div>

     
        {/* Wholesale Prices - مع إضافة نوع البيع (وحدة/كرتون) */}
<div style={{ marginTop: 12 }}>
  <h4>💰 Prix de Gros (Optionnel)</h4>
  {['demiGros', 'gros', 'superGros'].map((key) => {
    const labelMap = { demiGros: '🥈 Demi Gros', gros: '🏅 Gros', superGros: '👑 Super Gros' };
    const item = product.wholesalePrices[key];

    return (
      <div key={key} style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 12, 
        borderTop: '1px solid #ddd', 
        paddingTop: 12, 
        marginTop: 12,
        background: "#fafafa",
        padding: 16,
        borderRadius: 8
      }}>
        <h5 style={{ margin: 0, color: key === 'demiGros' ? '#ff9800' : key === 'gros' ? '#4caf50' : '#f44336' }}>
          {labelMap[key]}
        </h5>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {/* Prix */}
          <div style={{ flex: '1 1 25%' }}>
            <label>💰 Prix (DA)</label>
            <input
              type="number"
              step="0.01"
              placeholder="Prix spécial"
              value={item.price}
              onChange={(e) => setProduct({
                ...product,
                wholesalePrices: {
                  ...product.wholesalePrices,
                  [key]: { ...item, price: e.target.value },
                },
              })}
              style={inputStyle}
            />
          </div>

          {/* Type de vente (Unité/Carton) */}
          <div style={{ flex: '1 1 25%' }}>
            <label>📦 Type de vente</label>
            <select
              value={item.saleType}
              onChange={(e) => {
                const newSaleType = parseInt(e.target.value);
                setProduct({
                  ...product,
                  wholesalePrices: {
                    ...product.wholesalePrices,
                    [key]: { 
                      ...item, 
                      saleType: newSaleType,
                      // Reset les min quand on change le type
                      minQtyUnit: newSaleType === 0 ? 1 : 0,
                      minQtyBox: newSaleType === 1 ? 1 : 0
                    },
                  },
                });
              }}
              style={inputStyle}
            >
              <option value={0}>🏷️ Unité</option>
              <option value={1}>📦 Carton</option>
            </select>
          </div>

          {/* Quantité minimum - change selon le type */}
          {item.saleType === 0 && (
            <div style={{ flex: '1 1 25%' }}>
              <label>📏 Quantité minimum (Unités)</label>
              <input
                type="number"
                min="1"
                placeholder="Ex: 6"
                value={item.minQtyUnit}
                onChange={(e) => setProduct({
                  ...product,
                  wholesalePrices: {
                    ...product.wholesalePrices,
                    [key]: { ...item, minQtyUnit: parseInt(e.target.value) || 0, minQtyBox: 0 },
                  },
                })}
                style={inputStyle}
              />
              <small style={{ fontSize: "11px", color: "#666" }}>
                Minimum d'unités à commander
              </small>
            </div>
          )}

          {item.saleType === 1 && (
            <div style={{ flex: '1 1 25%' }}>
              <label>📏 Quantité minimum (Cartons)</label>
              <input
                type="number"
                min="1"
                placeholder="Ex: 2"
                value={item.minQtyBox}
                onChange={(e) => setProduct({
                  ...product,
                  wholesalePrices: {
                    ...product.wholesalePrices,
                    [key]: { ...item, minQtyBox: parseInt(e.target.value) || 0, minQtyUnit: 0 },
                  },
                })}
                style={inputStyle}
              />
              <small style={{ fontSize: "11px", color: "#666" }}>
                Minimum de cartons à commander
                {product.packaging && ` (${parseInt(item.minQtyBox) * parseInt(product.packaging)} unités)`}
              </small>
            </div>
          )}

          {/* Affichage du nombre d'unités total pour le carton */}
          {item.saleType === 1 && item.minQtyBox > 0 && product.packaging && (
            <div style={{ flex: '1 1 100%', fontSize: "12px", color: "#0891b2", marginTop: 5 }}>
              ℹ️ {item.minQtyBox} carton(s) = {parseInt(item.minQtyBox) * parseInt(product.packaging)} unités
            </div>
          )}
        </div>
      </div>
    );
  })}
</div>

        {/* Image */}
        <div style={{ display: "flex", flexDirection: "column", width: "100%", marginTop: 12 }}>
          <label>Image du produit (optionnel)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setProduct({ ...product, imageFile: e.target.files[0] })}
            style={{ marginTop: 5 }}
          />
        </div>

        {/* Submit Buttons */}
        <div style={{ width: "100%", marginTop: 20, display: "flex", gap: 10 }}>
          <button 
            type="submit" 
            style={{
              ...btnStyle,
              backgroundColor: !isFormValid() || isLoading || isImporting ? '#ccc' : '#007bff',
              cursor: !isFormValid() || isLoading || isImporting ? 'not-allowed' : 'pointer'
            }} 
            disabled={!isFormValid() || isLoading || isImporting}
          >
            {isLoading ? "Enregistrement..." : "Ajouter le produit"}
          </button>
          
          <button
            type="button"
            onClick={() => paramUid ? navigate(`/user/${uid}`) : navigate(-1)}
            style={{
              ...btnStyle,
              background: "#6c757d"
            }}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}